import React,{useImperativeHandle, useCallback, forwardRef} from 'react';
import ReactDOM from 'react-dom';
import {SegmentedControl, Image, Video} from 'polyphony/components/generic';
import BaseMediaPicker from './basemediapicker';
import {useStateWithCallback, useHover} from 'polyphony/hooks';

const TOP_PADDING = 14;

const styles = {
    segmented: {
        height: 34,
        borderRadius: 5,
        overflow: 'hidden',
        color: 'white',
        marginTop: 10,
        maxWidth: 200,
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    mediaContainer: {
        width: '100%',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
    },
    clearButton: {
        marginTop: 10
    },
    videoComponent: {
        objectFit: 'cover',
        display: 'block',
        maxWidth: '100%'
    },
    imageComponent: {
        objectFit: 'contain',
        maxWidth: '100%'
    },
    topOffset: {
        marginTop: TOP_PADDING
    },
    removeButton: {
        color: "#d94f4f",
        margin: 0,
        padding: '8px 0',
        boxShadow: "none",
        border: 0,
        borderRadius: 0,
        background: "none",
        outline: "none",
        textAlign: "left",
        textDecoration: "underline",
        transitionProperty: "border,background,color",
        transitionDuration: ".05s",
        transitionTimingFunction: "ease-in-out",
        cursor: 'pointer',
        fontSize: 13,
        lineHeight: '20px'
    },
    selectButton: {
        color: 'black',
        border: "1px dashed #a2aab2",
        backgroundColor: "#edeff0",
        lineHeight: "20px",
        padding: '8px 0',
        textAlign: 'center',
        transition: "all .1s ease-out",
        boxShadow: "0 0 0 0 #00a0d2",
        display: "block",
        width: "100%",
        textDecoration: "none",
        fontSize: 13,
        margin: 0,
        cursor: "pointer"
    }
};

// WordPress's select media button
const SelectMediaButton = ({style, ...props}) => {
    const [{hover, active}, ref] = useHover();
    return <a {...props} ref={ref} style={{...styles.topOffset, ...styles.selectButton, ...style, color: !hover ? undefined : active ? '#21759b' : 'gray'}}/>;

};

const MediaPicker = forwardRef(({media: initialMedia, fit: initialFit, onMediaChange, onFitChange, supportsFit}, ref) => {
    const [media, setMedia] = useStateWithCallback(initialMedia, onMediaChange, false);
    let [fit, setFit] = useStateWithCallback(initialFit, onFitChange, false);

    const clear = useCallback(() => {
        setFit(null);
        setMedia(null);
    }, [setFit, setMedia]);

    useImperativeHandle(ref, () => ({clear}), [clear]);

    const onChangeFit = useCallback(nf => setFit(nf), [setFit]);

    return <BaseMediaPicker selectedMedia={media} onSelect={setMedia}>
    {
        open => !media ? <SelectMediaButton onClick={open}>Select Media</SelectMediaButton>
                : <>
                      <div style={styles.mediaContainer} onClick={open}>
                          {media.type === 'image' && <Image style={styles.imageComponent} media={media} lazy />}
                          {media.type === 'video' && <Video style={styles.videoComponent} src={media.url} mimeType={media.mimeType} autoplay />}
                      </div>
                      {supportsFit && <SegmentedControl defaultValue={fit || 'cover'} color="gray" onChange={onChangeFit} style={styles.segmented}>
                          <SegmentedControl.Option title="Cover" value="cover" />
                          <SegmentedControl.Option title="Fit" value="fit" />
                      </SegmentedControl>}
                      <button type='button' style={{...styles.clearButton, ...styles.removeButton}} onClick={clear}>
                          Remove {media.type}
                      </button>
                  </>
    }
    </BaseMediaPicker>;
});

MediaPicker.openMediaPicker = BaseMediaPicker.openMediaPicker;
MediaPicker.SelectMediaButton = SelectMediaButton;
MediaPicker.renderIn = (container, { initialMedia:media, initialFit:fit, supportsFit, onMediaChange, onFitChange }) => {
    let r = React.createRef();
    let shouldClear = false;
    let jsx = <MediaPicker media={media}
                           fit={fit}
                           ref={r}
                           supportsFit={supportsFit === undefined ? false : supportsFit}
                           onMediaChange={onMediaChange}
                           onFitChange={onFitChange} />;
    ReactDOM.render(jsx, container, () => {
        if (shouldClear) {
            r.current.clear();
        }
    });
    return () => {
        if (r.current) r.current.clear();
        else           shouldClear = true;
    };
};

export default MediaPicker;

